import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Heading, Text, Flex, Spinner, useToast, Badge, VStack, HStack, Card, CardBody, Progress,
    Table, Thead, Tbody, Tr, Th, Td, TableContainer, Stat, StatLabel, StatNumber, StatHelpText,
    SimpleGrid, Button, Input, InputGroup, InputLeftElement, Select, Divider, Tooltip,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton, useDisclosure,
} from '@chakra-ui/react';
import { CheckCircleIcon, WarningIcon, SearchIcon, InfoIcon, TimeIcon } from '@chakra-ui/icons';
import Sidebar from '../../../components/Dashboard/Sidebar';
import { fetchTranslationReport } from '../../../api/reportService';
import { fetchTranslationInfo } from '../../../api/translationVerificationService';

const percent = (part, whole) => (whole > 0 ? (part / whole) * 100 : 0);

const getStatusBadge = (percentage) => {
    if (percentage >= 100) return { color: 'green', text: 'Complete' };
    if (percentage >= 75) return { color: 'blue', text: 'Near Complete' };
    if (percentage >= 50) return { color: 'yellow', text: 'In Progress' };
    if (percentage > 0) return { color: 'orange', text: 'Started' };
    return { color: 'gray', text: 'Pending' };
};

// Corrections Modal Component
const CorrectionsModal = ({ isOpen, onClose, surah, translator }) => (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
            <ModalHeader>
                Corrections for {surah?.tname || `Surah ${surah?.surah_id}`}
                {translator && <Text fontSize="sm" fontWeight="normal" color="gray.500">{translator}</Text>}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
                {surah?.with_corrections?.length ? (
                    <VStack spacing={6} align="stretch">
                        {surah.with_corrections.map((item) => (
                            <Card key={item.ayah_key} borderLeft="4px" borderColor="yellow.400">
                                <CardBody>
                                    <VStack align="stretch" spacing={3}>
                                        <HStack justify="space-between">
                                            <Text fontWeight="bold" color="gray.700">Ayah {item.ayah_index}</Text>
                                            <HStack>
                                                {item.is_approved && <Badge colorScheme="green">Approved</Badge>}
                                                <Badge colorScheme="yellow">
                                                    {item.corrections_count} {item.corrections_count === 1 ? 'Correction' : 'Corrections'}
                                                </Badge>
                                            </HStack>
                                        </HStack>
                                        <Text fontSize="sm" color="gray.600">
                                            Ayah Key: {item.ayah_key} · {item.verified_count} expert{item.verified_count === 1 ? '' : 's'} verified
                                        </Text>

                                        {item.text && (
                                            <Box>
                                                <Text fontSize="xs" fontWeight="bold" mb={1}>Current translation:</Text>
                                                <Text fontSize="sm" dir="auto" p={2} bg="gray.50" borderRadius="md">{item.text}</Text>
                                            </Box>
                                        )}

                                        <Divider />

                                        <VStack align="stretch" spacing={4}>
                                            {item.corrections.map((corr, idx) => (
                                                <Box key={idx} p={3} bg="yellow.50" borderRadius="md">
                                                    <VStack align="stretch" spacing={2}>
                                                        <HStack justify="space-between">
                                                            <Text fontSize="xs" color="gray.600" fontWeight="medium">
                                                                {corr.username || `Expert #${idx + 1}`}
                                                            </Text>
                                                            {corr.verified_at && (
                                                                <Text fontSize="xs" color="gray.500">
                                                                    {new Date(corr.verified_at).toLocaleString()}
                                                                </Text>
                                                            )}
                                                        </HStack>
                                                        {corr.corrections && (
                                                            <Box>
                                                                <Text fontSize="xs" fontWeight="bold" mb={1}>Suggested correction:</Text>
                                                                <Text fontSize="sm" dir="auto" p={2} bg="white" borderRadius="md">
                                                                    {corr.corrections}
                                                                </Text>
                                                            </Box>
                                                        )}
                                                        {corr.comments && (
                                                            <Box>
                                                                <Text fontSize="xs" fontWeight="bold" mb={1}>Comments:</Text>
                                                                <Text fontSize="sm" color="gray.700">{corr.comments}</Text>
                                                            </Box>
                                                        )}
                                                        {!corr.corrections && !corr.comments && (
                                                            <Text fontSize="sm" color="gray.500">Rejected without a comment.</Text>
                                                        )}
                                                    </VStack>
                                                </Box>
                                            ))}
                                        </VStack>
                                    </VStack>
                                </CardBody>
                            </Card>
                        ))}
                    </VStack>
                ) : (
                    <Text textAlign="center" color="gray.500" py={8}>No corrections found for this Surah</Text>
                )}
            </ModalBody>
            <ModalFooter>
                <Button colorScheme="blue" onClick={onClose}>Close</Button>
            </ModalFooter>
        </ModalContent>
    </Modal>
);

// Stats Card Component
const StatsCard = ({ label, value, subValue, icon, colorScheme = 'blue' }) => (
    <Card>
        <CardBody>
            <Stat>
                <Flex justify="space-between" align="center">
                    <Box>
                        <StatLabel color="gray.600">{label}</StatLabel>
                        <StatNumber fontSize="3xl" color={`${colorScheme}.600`}>{value}</StatNumber>
                        {subValue && <StatHelpText mb={0}>{subValue}</StatHelpText>}
                    </Box>
                    <Box p={3} bg={`${colorScheme}.100`} borderRadius="full" color={`${colorScheme}.600`}>
                        {icon}
                    </Box>
                </Flex>
            </Stat>
        </CardBody>
    </Card>
);

const ProgressCell = ({ value }) => {
    const status = getStatusBadge(value);
    return (
        <VStack align="stretch" spacing={1}>
            <HStack>
                <Badge colorScheme={status.color} fontSize="xs">{status.text}</Badge>
                <Text fontSize="xs" color="gray.600">{value.toFixed(1)}%</Text>
            </HStack>
            <Progress value={value} size="xs" colorScheme={status.color} borderRadius="full" />
        </VStack>
    );
};

// Surah Row Component
const SurahRow = ({ surah, onViewCorrections }) => {
    // Expert-verified ayahs are the ones approved plus the ones waiting for approval
    const verificationPercentage = percent(surah.approved + surah.pending_approval, surah.total);
    const approvalPercentage = percent(surah.approved, surah.total);
    const corrections = surah.with_corrections.length;

    return (
        <Tr _hover={{ bg: 'gray.50' }}>
            <Td fontWeight="medium">{surah.surah_id}</Td>
            <Td>
                <VStack align="start" spacing={0}>
                    <Text fontWeight="medium">{surah.name}</Text>
                    <Text fontSize="xs" color="gray.500">{surah.tname} - {surah.ename}</Text>
                </VStack>
            </Td>
            <Td>
                {surah.type && (
                    <Badge colorScheme={surah.type === 'Meccan' ? 'purple' : 'teal'} fontSize="xs">{surah.type}</Badge>
                )}
            </Td>
            <Td isNumeric>{surah.total}</Td>
            <Td isNumeric><Text color="green.600" fontWeight="semibold">{surah.approved}</Text></Td>
            <Td isNumeric><Text color="blue.600" fontWeight="semibold">{surah.pending_approval}</Text></Td>
            <Td isNumeric><Text color="orange.600" fontWeight="semibold">{surah.pending_verification}</Text></Td>
            <Td><ProgressCell value={verificationPercentage} /></Td>
            <Td><ProgressCell value={approvalPercentage} /></Td>
            <Td textAlign="center">
                {corrections > 0 ? (
                    <Tooltip label="View corrections">
                        <Button size="sm" colorScheme="yellow" variant="outline" leftIcon={<WarningIcon />} onClick={() => onViewCorrections(surah)}>
                            {corrections}
                        </Button>
                    </Tooltip>
                ) : (
                    <Text color="gray.400" fontSize="sm">-</Text>
                )}
            </Td>
        </Tr>
    );
};

// Main Report Component
const TranslationReportPage = () => {
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const [translationInfos, setTranslationInfos] = useState([]);
    const [translationId, setTranslationId] = useState('');
    const [loading, setLoading] = useState(true);
    const [reportData, setReportData] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSurah, setSelectedSurah] = useState(null);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    useEffect(() => {
        fetchTranslationInfo()
            .then((infos) => {
                setTranslationInfos(infos || []);
                setTranslationId(String(infos?.[0]?._id ?? '1'));
            })
            .catch(() => setTranslationId('1'));
    }, []);

    const loadReport = useCallback(async () => {
        if (!translationId) return;
        setLoading(true);
        try {
            setReportData(await fetchTranslationReport(translationId));
        } catch (err) {
            toast({
                title: 'Failed to load report',
                description: err.response?.data?.detail || err.message || 'An error occurred while fetching the report',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
            setReportData({ summary: [], totals: null });
        } finally {
            setLoading(false);
        }
    }, [translationId, toast]);

    useEffect(() => {
        loadReport();
    }, [loadReport]);

    const handleViewCorrections = (surah) => {
        setSelectedSurah(surah);
        onOpen();
    };

    const searchLower = searchTerm.toLowerCase();
    const filteredSurahs = (reportData?.summary || []).filter((surah) =>
        surah.name?.toLowerCase().includes(searchLower) ||
        surah.tname?.toLowerCase().includes(searchLower) ||
        surah.ename?.toLowerCase().includes(searchLower) ||
        String(surah.surah_id).includes(searchTerm)
    );

    const totals = reportData?.totals;
    const translator = translationInfos.find((t) => String(t._id) === translationId)?.translator;
    const verifiedTotal = totals ? totals.approved + totals.pending_approval : 0;

    return (
        <Flex direction="row" h="100vh">
            <Sidebar isCollapsed={sidebarCollapsed} toggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)} />
            <Box
                pl={sidebarCollapsed ? '60px' : '250px'}
                w="full"
                overflowY="auto"
                pb={8}
                transition="padding-left 0.2s ease"
            >
                <Box minH="100vh" bg="gray.50" p={8}>
                    <VStack spacing={6} align="stretch">
                        {/* Header */}
                        <Flex justify="space-between" align={{ base: 'stretch', md: 'flex-end' }} gap={4} direction={{ base: 'column', md: 'row' }}>
                            <Box>
                                <Heading size="xl" mb={2}>Translation Verification Report</Heading>
                                <Text color="gray.600">
                                    Track expert verification and verifier approval of each translation across all Surahs
                                </Text>
                            </Box>
                            <Select
                                maxW={{ md: '320px' }}
                                bg="white"
                                value={translationId}
                                onChange={(e) => setTranslationId(e.target.value)}
                                aria-label="Translation"
                            >
                                {translationInfos.length === 0 && <option value={translationId}>Translation {translationId}</option>}
                                {translationInfos.map((info) => (
                                    <option key={info._id} value={String(info._id)}>{info.translator}</option>
                                ))}
                            </Select>
                        </Flex>

                        {loading ? (
                            <Flex justify="center" py={20}>
                                <VStack spacing={4}>
                                    <Spinner size="xl" color="blue.500" thickness="4px" />
                                    <Text color="gray.600">Loading translation report...</Text>
                                </VStack>
                            </Flex>
                        ) : (
                            <>
                                {/* Overall Statistics */}
                                {totals && (
                                    <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                                        <StatsCard
                                            label="Total Ayahs"
                                            value={totals.total.toLocaleString()}
                                            subValue={`${totals.experts} expert${totals.experts === 1 ? '' : 's'} took part`}
                                            icon={<InfoIcon boxSize={6} />}
                                            colorScheme="blue"
                                        />
                                        <StatsCard
                                            label="Approved"
                                            value={totals.approved.toLocaleString()}
                                            subValue={`${percent(totals.approved, totals.total).toFixed(1)}% Complete`}
                                            icon={<CheckCircleIcon boxSize={6} />}
                                            colorScheme="green"
                                        />
                                        <StatsCard
                                            label="Pending Approval"
                                            value={totals.pending_approval.toLocaleString()}
                                            subValue="Verified by every expert who checked"
                                            icon={<TimeIcon boxSize={6} />}
                                            colorScheme="purple"
                                        />
                                        <StatsCard
                                            label="Pending Verification"
                                            value={totals.pending_verification.toLocaleString()}
                                            subValue={`${totals.with_corrections} with corrections`}
                                            icon={<WarningIcon boxSize={6} />}
                                            colorScheme="orange"
                                        />
                                    </SimpleGrid>
                                )}

                                {/* Overall Progress */}
                                {totals && (
                                    <Card>
                                        <CardBody>
                                            <Heading size="md" mb={4}>Overall Progress</Heading>
                                            <VStack spacing={4} align="stretch">
                                                <Box>
                                                    <Flex justify="space-between" mb={2}>
                                                        <Text fontSize="sm" fontWeight="medium">Expert Verification Progress</Text>
                                                        <Text fontSize="sm" color="gray.600">
                                                            {verifiedTotal} / {totals.total} ({percent(verifiedTotal, totals.total).toFixed(1)}%)
                                                        </Text>
                                                    </Flex>
                                                    <Progress value={percent(verifiedTotal, totals.total)} colorScheme="green" borderRadius="full" size="md" />
                                                </Box>
                                                <Box>
                                                    <Flex justify="space-between" mb={2}>
                                                        <Text fontSize="sm" fontWeight="medium">Verifier Approval Progress</Text>
                                                        <Text fontSize="sm" color="gray.600">
                                                            {totals.approved} / {totals.total} ({percent(totals.approved, totals.total).toFixed(1)}%)
                                                        </Text>
                                                    </Flex>
                                                    <Progress value={percent(totals.approved, totals.total)} colorScheme="purple" borderRadius="full" size="md" />
                                                </Box>
                                            </VStack>
                                        </CardBody>
                                    </Card>
                                )}

                                {/* Surah-wise Progress Table */}
                                <Card>
                                    <CardBody>
                                        <Flex justify="space-between" align="center" mb={4} gap={4} wrap="wrap">
                                            <Heading size="md">Surah-wise Progress</Heading>
                                            <InputGroup maxW="300px">
                                                <InputLeftElement pointerEvents="none">
                                                    <SearchIcon color="gray.400" />
                                                </InputLeftElement>
                                                <Input
                                                    placeholder="Search surah..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                />
                                            </InputGroup>
                                        </Flex>

                                        <TableContainer>
                                            <Table variant="simple" size="sm">
                                                <Thead>
                                                    <Tr>
                                                        <Th>#</Th>
                                                        <Th>Surah Name</Th>
                                                        <Th>Type</Th>
                                                        <Th isNumeric>Total</Th>
                                                        <Th isNumeric>Approved</Th>
                                                        <Th isNumeric>Pending Approval</Th>
                                                        <Th isNumeric>Pending Verification</Th>
                                                        <Th>Expert Verification</Th>
                                                        <Th>Verifier Approval</Th>
                                                        <Th textAlign="center">Corrections</Th>
                                                    </Tr>
                                                </Thead>
                                                <Tbody>
                                                    {filteredSurahs.map((surah) => (
                                                        <SurahRow key={surah.surah_id} surah={surah} onViewCorrections={handleViewCorrections} />
                                                    ))}
                                                </Tbody>
                                            </Table>
                                        </TableContainer>

                                        {filteredSurahs.length === 0 && (
                                            <Flex justify="center" py={8}>
                                                <Text color="gray.500">
                                                    {searchTerm ? 'No surahs found matching your search' : 'No data available for this translation'}
                                                </Text>
                                            </Flex>
                                        )}
                                    </CardBody>
                                </Card>
                            </>
                        )}
                    </VStack>

                    <CorrectionsModal isOpen={isOpen} onClose={onClose} surah={selectedSurah} translator={translator} />
                </Box>
            </Box>
        </Flex>
    );
};

export default TranslationReportPage;
